import { safeboxClient } from 'myNet'
import { store as reduxStore } from 'index'

const ROUTE = '/v1/user-settings'

type Locale = 'ko' | 'en' | 'ja'
type AutoLock = string // 또는 정확한 타입 지정 (예: '1', '2', 등)
type RecoveryKeyOption = 'email' | 'local' | null

interface Setting {
  label: string
  content: string
}

const body = (
  local?: Locale,
  autoLock?: AutoLock,
  recoveryKeyToEmail?: string | null,
  zeroDarkWebSettings?: any,
): any => {
  const settings: Setting[] = []

  const localeMap: Record<Locale, string> = {
    ko: 'ko-KR',
    en: 'en-US',
    ja: 'ja-JP',
  }

  if (local) {
    settings.push({ label: 'language', content: local })
  }

  if (autoLock) {
    settings.push({ label: 'auto_lock', content: autoLock })
  }

  if (recoveryKeyToEmail != null) {
    settings.push({ label: 'send_recovery_key_to_email', content: recoveryKeyToEmail })
  }

  const result = {
    ...(settings.length > 0 ? { global_setting: settings } : null),
    ...(zeroDarkWebSettings && zeroDarkWebSettings.length > 0
      ? { personal_setting: zeroDarkWebSettings }
      : null),
  }

  function getRecoverKeyEnabled(): RecoveryKeyOption {
    if (recoveryKeyToEmail != null) {
      return recoveryKeyToEmail !== 'email' ? 'local' : 'email'
    }
    return null
  }

  // const reduxSettings =
  //   Array.isArray(zeroDarkWebSettings) && zeroDarkWebSettings.length > 0
  //     ? zeroDarkWebSettings.reduce((acc: Record<string, any>, item: any) => {
  //         if (item && item.label !== undefined && item.content !== undefined) {
  //           acc[item.label] = item.content
  //         }
  //         return acc
  //       }, {})
  //     : undefined

  reduxStore.dispatch({
    type: 'settings/SET_SETTINGS',
    payload: {
      locale: local ? localeMap[local] : undefined,
      umkDuration: autoLock ? Number(autoLock) * 60 * 1000 : undefined,
      recoverKeyEnabled: getRecoverKeyEnabled(),
      // zeroDarkWebSettings: reduxSettings || undefined,
    },
  })

  return result
}

interface SettingsRequest {
  local?: Locale
  autoLock?: string
  recoveryKeyToEmail?: string | null
  zeroDarkWebSettings?: any
}

interface ApiResponse<T = any> {
  code: number
  message: string
  data: T
  [key: string]: any
}

const SettingsService = {
  put: async ({
    local,
    autoLock,
    recoveryKeyToEmail,
    zeroDarkWebSettings,
  }: SettingsRequest): Promise<ApiResponse> => {
    const response = await safeboxClient.put(
      `${ROUTE}`,
      body(local, autoLock, recoveryKeyToEmail, zeroDarkWebSettings),
    )
    return response.data
  },
  create: async ({
    local,
    autoLock,
    recoveryKeyToEmail,
  }: SettingsRequest): Promise<ApiResponse> => {
    const response = await safeboxClient.post(`${ROUTE}`, {
      settings: body(local, autoLock, recoveryKeyToEmail),
    })
    return response.data
  },
  get: async (): Promise<ApiResponse> => {
    const response = await safeboxClient.get(`${ROUTE}`)
    return response.data
  },
  // clipboard_clear 는 신규 enum 라벨이라 backend 미배포 window 에 422 가능.
  // 다른 설정(language/auto_lock 등)과 분리된 별도 PUT 으로 격리하고, 실패해도 throw 안 함.
  // 로컬(redux/localStorage) 반영은 PUT 성공 또는 422(미배포) 시에만 수행한다.
  //   - 422: backend enum 미지원(배포 순서) → 낙관적 반영(서버 저장만 보류).
  //   - 401/500/네트워크: 진짜 오류 → 로컬 미반영(다음 getSettings 와의 불일치 방지).
  // 반환값(applied): 로컬에 반영됐는지 여부. 호출측이 이 값으로 익스텐션 통지 여부를 게이팅해
  //   redux/web 스케줄러/익스텐션 캐시 3자가 어긋나지 않게 한다.
  // minutes: '0'=Never(off), '1'~'5'=분.
  putClipboardClear: async (minutes: string): Promise<boolean> => {
    const applyLocal = () => {
      reduxStore.dispatch({
        type: 'settings/SET_SETTINGS',
        payload: {
          clipboardClearMs: minutes && minutes !== '0' ? Number(minutes) * 60 * 1000 : 0,
        },
      })
    }
    try {
      await safeboxClient.put(`${ROUTE}`, {
        global_setting: [{ label: 'clipboard_clear', content: minutes }],
      })
      // 서버 저장이 확정된 뒤에만 로컬에 반영해 상태 불일치를 막는다.
      applyLocal()
      return true
    } catch (e) {
      // best-effort + 무알림 정책상 사용자 토스트는 띄우지 않는다.
      // (axios.isAxiosError 가드는 jest 가 axios ESM 을 transform 못 해 테스트가 깨지므로 미사용.)
      const status =
        e != null && typeof e === 'object' && 'response' in e
          ? (e as { response?: { status?: number } }).response?.status
          : undefined
      // TODO: backend clipboard_clear enum 배포 완료 후 이 422 폴백 제거(이후 422 는 진짜 오류로 취급).
      if (status === 422) {
        applyLocal()
        return true
      }
      console.warn('[clipboardClear] setting save failed, status:', status)
      return false
    }
  },
}

export default SettingsService
